import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Button from '@legacy-vfuk/core-button';
import {
  Grid,
  Input,
  Select,
  InputDiv,
  ErrorMessage,
  ErrorInput,
  Asterisk,
  SectionTitle,
  SectionDescription,
  EmailInfoMessage,
  AgreementSection,
  AgreementTitle,
  AgreementText,
  ActionButtonWrapper,
  InfoBox,
  TimelineStep,
  TimelineDot,
  StepContent,
} from './EditMicrosoftDataForm.styles';
import { opcoSettings } from '../../../../../../config/opcoSettings';
import { postMca, getMca } from '@/services/product.services';
import { useAppSelector } from '@/store';
import axios from 'axios';
import { InLineNotification } from '@uk-source-web/inline-notification';
import { Icon } from '@legacy-vfuk/core-icon';
import { translatedMDFValues } from './translatedValues';

const OPCO = process.env.GATSBY_OPCO;

interface MCAFormSectionProps {
  accountId: string;
  needsMCA?: boolean;
  onMcaVerified?: (verified: boolean) => void;
}

interface MCAFormData {
  mcaFirstName: string;
  mcaLastName: string;
  mcaPhoneNumber: string;
  mcaEmail: string;
  mcaCountry: string;
}

export const MCAFormSection = ({ accountId, needsMCA, onMcaVerified }: MCAFormSectionProps) => {
  const { user } = useAppSelector(({ userSession }) => userSession);
  // MCA Form State
  const [mcaFormData, setMcaFormData] = useState<MCAFormData>({
    mcaFirstName: '',
    mcaLastName: '',
    mcaPhoneNumber: '',
    mcaEmail: '',
    mcaCountry: '',
  });

  const [mcaErrors, setMcaErrors] = useState<Record<string, string>>({});
  const [mcaTouched, setMcaTouched] = useState<Record<string, boolean>>({});
  const [agreementStatus, setAgreementStatus] = useState<'PENDING' | 'ACCEPTED' | 'ACTIVE'>('PENDING');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isOpeningAgreement, setIsOpeningAgreement] = useState(false);
  const [showVerificationError, setShowVerificationError] = useState(false);

  // Helper function to check if MCA status is verified (ACCEPTED or ACTIVE)
  const isMcaVerified = useCallback((status: string | undefined): boolean => {
    return status === 'ACCEPTED' || status === 'ACTIVE';
  }, []);

  // MCA Form Validation Functions
  const validateMcaName = useCallback((name: string, fieldType: 'firstName' | 'lastName'): string | undefined => {
    if (!name.trim()) {
      return fieldType === 'firstName' ? translatedMDFValues.firstNameRequired : translatedMDFValues.lastNameRequired;
    }
    if (name.trim().length < 2) {
      return fieldType === 'firstName' ? translatedMDFValues.firstNameMinLength : translatedMDFValues.lastNameMinLength;
    }
    if (!/^[A-Za-z\s\-']+$/.test(name)) {
      return fieldType === 'firstName' ? translatedMDFValues.firstNameInvalidChars : translatedMDFValues.lastNameInvalidChars;
    }
    return undefined;
  }, []);

  const validateMcaEmail = useCallback((email: string): string | undefined => {
    if (!email.trim()) {
      return translatedMDFValues.emailRequired;
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return translatedMDFValues.mcaEmailInvalid;
    }
    if (/@microsoft\.com$/i.test(email)) {
      return translatedMDFValues.microsoftEmailNotAllowed;
    }
    return undefined;
  }, []);

  const validateMcaPhoneNumber = useCallback((phone: string): string | undefined => {
    if (!phone.trim()) {
      return translatedMDFValues.phoneRequired;
    }
    const digitsOnly = phone.replace(/[\s\-()]/g, '');
    if (digitsOnly.length < 10) {
      return translatedMDFValues.phoneMinLength;
    }
    const phoneRegex = opcoSettings[OPCO as keyof typeof opcoSettings].phoneRegex;
    if (!phoneRegex.test(digitsOnly)) {
      return translatedMDFValues.mcaPhoneInvalid;
    }
    return undefined;
  }, []);

  const validateMcaCountry = useCallback((country: string): string | undefined => {
    if (!country) {
      return translatedMDFValues.countryRequired;
    }
    return undefined;
  }, []);

  const formatMcaPhoneNumber = useCallback((value: string): string => {
    const cleaned = value.replace(/[^\d+]/g, '');
    const hasPlus = cleaned.startsWith('+');
    const digits = cleaned.replace(/\+/g, '');
    let formatted = '';

    if (digits.length <= 3) {
      formatted = digits;
    } else if (digits.length <= 6) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    } else {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10, 14)}`;
    }
    return hasPlus ? `+${formatted}` : formatted;
  }, []);

  // Load MCA saved data from localStorage and validate
  useEffect(() => {
    const savedMcaData = localStorage.getItem(`${accountId}_mcaFormData`);

    if (savedMcaData) {
      const parsedData = JSON.parse(savedMcaData);
      setMcaFormData(parsedData);
      
      // Validate loaded data to show errors if invalid
      const newErrors: Record<string, string> = {};
      const firstNameError = validateMcaName(parsedData.mcaFirstName || '', 'firstName');
      const lastNameError = validateMcaName(parsedData.mcaLastName || '', 'lastName');
      const emailError = validateMcaEmail(parsedData.mcaEmail || '');
      const phoneError = validateMcaPhoneNumber(parsedData.mcaPhoneNumber || '');
      const countryError = validateMcaCountry(parsedData.mcaCountry || '');

      if (firstNameError) newErrors.mcaFirstName = firstNameError;
      if (lastNameError) newErrors.mcaLastName = lastNameError;
      if (emailError) newErrors.mcaEmail = emailError;
      if (phoneError) newErrors.mcaPhoneNumber = phoneError;
      if (countryError) newErrors.mcaCountry = countryError;

      if (Object.keys(newErrors).length > 0) {
        setMcaErrors(newErrors);
        // Mark all fields as touched if there are errors
        setMcaTouched({
          mcaFirstName: !!parsedData.mcaFirstName,
          mcaLastName: !!parsedData.mcaLastName,
          mcaPhoneNumber: !!parsedData.mcaPhoneNumber,
          mcaEmail: !!parsedData.mcaEmail,
          mcaCountry: !!parsedData.mcaCountry,
        });
      }
    }
  }, [accountId, validateMcaName, validateMcaEmail, validateMcaPhoneNumber, validateMcaCountry]);

  // Fetch agreement status from API on mount
  useEffect(() => {
    const fetchAgreementStatus = async () => {
      setIsVerifying(true);

      if (!user?.csrfToken) return;
      
      try {
        const response = await getMca(user.csrfToken, needsMCA ?? true);
        if (response && isMcaVerified(response.mcaStatus)) {
          // Set status to ACTIVE if it's ACTIVE, otherwise ACCEPTED
          setAgreementStatus(response.mcaStatus === 'ACTIVE' ? 'ACTIVE' : 'ACCEPTED');
          onMcaVerified?.(true);
        } else {
          setAgreementStatus('PENDING');
        }
      } catch (error) {
        console.error('Error fetching agreement status:', error);
        setAgreementStatus('PENDING');
      } finally {
        setIsVerifying(false);
      }
    };

    fetchAgreementStatus();
  }, [accountId, user?.csrfToken, needsMCA, onMcaVerified, isMcaVerified]);

  // MCA Form Handlers
  const handleMcaInputChange = useCallback(
    (field: keyof MCAFormData) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let value = event.target.value;
      if (field === 'mcaPhoneNumber') {
        value = formatMcaPhoneNumber(value);
      }
      const sanitizedValue = value.replace(/[<>{}]/g, '');
      setMcaFormData(prev => {
        const updated = { ...prev, [field]: sanitizedValue };
        localStorage.setItem(`${accountId}_mcaFormData`, JSON.stringify(updated));
        return updated;
      });
      
      // Validate in real-time if field has been touched
      if (mcaTouched[field]) {
        let error: string | undefined;
        switch (field) {
          case 'mcaFirstName':
            error = validateMcaName(sanitizedValue, 'firstName');
            break;
          case 'mcaLastName':
            error = validateMcaName(sanitizedValue, 'lastName');
            break;
          case 'mcaEmail':
            error = validateMcaEmail(sanitizedValue);
            break;
          case 'mcaPhoneNumber':
            error = validateMcaPhoneNumber(sanitizedValue);
            break;
          case 'mcaCountry':
            error = validateMcaCountry(sanitizedValue);
            break;
        }
        setMcaErrors(prev => ({ ...prev, [field]: error || '' }));
      } else if (mcaErrors[field]) {
        // Clear error if field hasn't been touched yet
        setMcaErrors(prev => ({ ...prev, [field]: '' }));
      }
    },
    [accountId, mcaErrors, mcaTouched, mcaFormData, formatMcaPhoneNumber, validateMcaName, validateMcaEmail, validateMcaPhoneNumber, validateMcaCountry]
  );

  const handleMcaBlur = useCallback(
    (field: keyof MCAFormData) => () => {
      setMcaTouched(prev => ({ ...prev, [field]: true }));
      let error: string | undefined;
      switch (field) {
        case 'mcaFirstName':
          error = validateMcaName(mcaFormData.mcaFirstName, 'firstName');
          break;
        case 'mcaLastName':
          error = validateMcaName(mcaFormData.mcaLastName, 'lastName');
          break;
        case 'mcaEmail':
          error = validateMcaEmail(mcaFormData.mcaEmail);
          break;
        case 'mcaPhoneNumber':
          error = validateMcaPhoneNumber(mcaFormData.mcaPhoneNumber);
          break;
        case 'mcaCountry':
          error = validateMcaCountry(mcaFormData.mcaCountry);
          break;
      }
      if (error) {
        setMcaErrors(prev => ({ ...prev, [field]: error }));
      } else {
        setMcaErrors(prev => ({ ...prev, [field]: '' }));
      }
    },
    [mcaFormData, validateMcaName, validateMcaEmail, validateMcaPhoneNumber, validateMcaCountry]
  );

  const validateMcaForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const firstNameError = validateMcaName(mcaFormData.mcaFirstName, 'firstName');
    const lastNameError = validateMcaName(mcaFormData.mcaLastName, 'lastName');
    const emailError = validateMcaEmail(mcaFormData.mcaEmail);
    const phoneError = validateMcaPhoneNumber(mcaFormData.mcaPhoneNumber);
    const countryError = validateMcaCountry(mcaFormData.mcaCountry);

    if (firstNameError) newErrors.mcaFirstName = firstNameError;
    if (lastNameError) newErrors.mcaLastName = lastNameError;
    if (emailError) newErrors.mcaEmail = emailError;
    if (phoneError) newErrors.mcaPhoneNumber = phoneError;
    if (countryError) newErrors.mcaCountry = countryError;

    setMcaErrors(newErrors);
    setMcaTouched({
      mcaFirstName: true,
      mcaLastName: true,
      mcaPhoneNumber: true,
      mcaEmail: true,
      mcaCountry: true,
    });

    return Object.keys(newErrors).length === 0;
  }, [mcaFormData, validateMcaName, validateMcaEmail, validateMcaPhoneNumber, validateMcaCountry]);

  const handleOpenAgreement = useCallback(async () => {
    // Validate form before proceeding
    if (!validateMcaForm()) {
      alert(translatedMDFValues.fillAllFieldsAlert);
      return;
    }

    setIsOpeningAgreement(true);

    try {
      const countryMap: Record<string, string> = {
        IRL: 'Ireland',
        PRT: 'Portugal',
      };

      const payload = {
        country: countryMap[mcaFormData.mcaCountry] || mcaFormData.mcaCountry,
        firstName: mcaFormData.mcaFirstName,
        lastName: mcaFormData.mcaLastName,
        email: mcaFormData.mcaEmail,
        phoneNumber: mcaFormData.mcaPhoneNumber.replace(/\s+/g, ''),
      };

      // Post MCA data to get the agreement URL
      const response = await postMca(payload, user?.csrfToken || '');

      if (response && !axios.isAxiosError(response) && response && typeof response === 'object' && 'data' in response) {
        const responseData = response.data as { url?: string };

        if (responseData?.url) {
          // Open the agreement URL in a new tab
          window.open(responseData.url, '_blank', 'noopener,noreferrer');
          
          // Clear any previous verification error when opening a new agreement
          setShowVerificationError(false);
        } else {
          console.error('Failed to get MCA URL - missing URL in response', response);
          alert(translatedMDFValues.agreementUrlFailedAlert);
        }
      } else {
        console.error('Failed to get MCA URL - invalid response', response);
        alert(translatedMDFValues.agreementUrlFailedAlert);
      }
    } catch (error) {
      console.error('Error opening agreement:', error);
      alert(translatedMDFValues.agreementErrorAlert);
    } finally {
      setIsOpeningAgreement(false);
    }
  }, [validateMcaForm, accountId, mcaFormData, user]);

  const handleVerifyStatus = useCallback(async () => {
    setIsVerifying(true);
    setShowVerificationError(false);
    
    try {
      // Call GET /mca
      const response = await getMca(user?.csrfToken || '', needsMCA ?? true);

      if (response && isMcaVerified(response.mcaStatus)) {
        // Set status to ACTIVE if it's ACTIVE, otherwise ACCEPTED
        setAgreementStatus(response.mcaStatus === 'ACTIVE' ? 'ACTIVE' : 'ACCEPTED');
        setShowVerificationError(false);
        onMcaVerified?.(true);
      } else {
        // Still pending - show error message
        setAgreementStatus('PENDING');
        setShowVerificationError(true);
      }
    } catch (error) {
      console.error('Error verifying agreement:', error);
      setShowVerificationError(true);
    } finally {
      setIsVerifying(false);
    }
  }, [user, needsMCA, onMcaVerified, isMcaVerified]);

  const isStepCompleted = useCallback(
    (step: number): boolean => {
      if (step === 1) {
        // Check if all required fields are filled
        const allFieldsFilled = Object.values(mcaFormData).every(value => value.trim() !== '');
        // Check if there are no validation errors
        const noErrors = Object.values(mcaErrors).every(error => !error);
        return allFieldsFilled && noErrors;
      }
      if (step === 2) {
        return isMcaVerified(agreementStatus);
      }
      if (step === 3) {
        return isMcaVerified(agreementStatus);
      }
      return false;
    },
    [mcaFormData, mcaErrors, agreementStatus, isMcaVerified]
  );

  return (
    <>
      {/* Step 1: Customer Information */}
      <TimelineStep noPaddingBottom>
        <StepContent>
          <SectionTitle>{translatedMDFValues.customerInformation}</SectionTitle>
          <SectionDescription>{translatedMDFValues.customerInformationDescription}</SectionDescription>

          <Grid>
            {/* MCA Form Fields */}
            <InputDiv>
              <label htmlFor="mcaFirstName">
                {translatedMDFValues.firstName} <Asterisk>*</Asterisk>
              </label>
              {mcaTouched.mcaFirstName && mcaErrors.mcaFirstName ? (
                <ErrorInput
                  id="mcaFirstName"
                  name="mcaFirstName"
                  type="text"
                  value={mcaFormData.mcaFirstName}
                  onChange={handleMcaInputChange('mcaFirstName')}
                  onBlur={handleMcaBlur('mcaFirstName')}
                  placeholder={translatedMDFValues.enterFirstNamePlaceholder}
                />
              ) : (
                <Input
                  id="mcaFirstName"
                  name="mcaFirstName"
                  type="text"
                  value={mcaFormData.mcaFirstName}
                  onChange={handleMcaInputChange('mcaFirstName')}
                  onBlur={handleMcaBlur('mcaFirstName')}
                  placeholder={translatedMDFValues.enterFirstNamePlaceholder}
                />
              )}
              {mcaTouched.mcaFirstName && mcaErrors.mcaFirstName && (
                <ErrorMessage>{mcaErrors.mcaFirstName}</ErrorMessage>
              )}
            </InputDiv>

            <InputDiv>
              <label htmlFor="mcaLastName">
                {translatedMDFValues.lastName} <Asterisk>*</Asterisk>
              </label>
              {mcaTouched.mcaLastName && mcaErrors.mcaLastName ? (
                <ErrorInput
                  id="mcaLastName"
                  name="mcaLastName"
                  type="text"
                  value={mcaFormData.mcaLastName}
                  onChange={handleMcaInputChange('mcaLastName')}
                  onBlur={handleMcaBlur('mcaLastName')}
                  placeholder={translatedMDFValues.enterLastNamePlaceholder}
                />
              ) : (
                <Input
                  id="mcaLastName"
                  name="mcaLastName"
                  type="text"
                  value={mcaFormData.mcaLastName}
                  onChange={handleMcaInputChange('mcaLastName')}
                  onBlur={handleMcaBlur('mcaLastName')}
                  placeholder={translatedMDFValues.enterLastNamePlaceholder}
                />
              )}
              {mcaTouched.mcaLastName && mcaErrors.mcaLastName && <ErrorMessage>{mcaErrors.mcaLastName}</ErrorMessage>}
            </InputDiv>

            <InputDiv>
              <label htmlFor="mcaPhoneNumber">
                {translatedMDFValues.phoneNumber} <Asterisk>*</Asterisk>
              </label>
              {mcaTouched.mcaPhoneNumber && mcaErrors.mcaPhoneNumber ? (
                <ErrorInput
                  id="mcaPhoneNumber"
                  name="mcaPhoneNumber"
                  type="tel"
                  value={mcaFormData.mcaPhoneNumber}
                  onChange={handleMcaInputChange('mcaPhoneNumber')}
                  onBlur={handleMcaBlur('mcaPhoneNumber')}
                  placeholder={translatedMDFValues.enterPhoneNumberPlaceholder}
                />
              ) : (
                <Input
                  id="mcaPhoneNumber"
                  name="mcaPhoneNumber"
                  type="tel"
                  value={mcaFormData.mcaPhoneNumber}
                  onChange={handleMcaInputChange('mcaPhoneNumber')}
                  onBlur={handleMcaBlur('mcaPhoneNumber')}
                  placeholder={translatedMDFValues.enterPhoneNumberPlaceholder}
                />
              )}
              {mcaTouched.mcaPhoneNumber && mcaErrors.mcaPhoneNumber && (
                <ErrorMessage>{mcaErrors.mcaPhoneNumber}</ErrorMessage>
              )}
            </InputDiv>

            <InputDiv>
              <label htmlFor="mcaEmail">
                {translatedMDFValues.email} <Asterisk>*</Asterisk>
              </label>
              {mcaTouched.mcaEmail && mcaErrors.mcaEmail ? (
                <ErrorInput
                  id="mcaEmail"
                  name="mcaEmail"
                  type="email"
                  value={mcaFormData.mcaEmail}
                  onChange={handleMcaInputChange('mcaEmail')}
                  onBlur={handleMcaBlur('mcaEmail')}
                  placeholder={translatedMDFValues.enterEmailAddressPlaceholder}
                />
              ) : (
                <Input
                  id="mcaEmail"
                  name="mcaEmail"
                  type="email"
                  value={mcaFormData.mcaEmail}
                  onChange={handleMcaInputChange('mcaEmail')}
                  onBlur={handleMcaBlur('mcaEmail')}
                  placeholder={translatedMDFValues.enterEmailAddressPlaceholder}
                />
              )}
              {mcaTouched.mcaEmail && mcaErrors.mcaEmail && <ErrorMessage>{mcaErrors.mcaEmail}</ErrorMessage>}
              <EmailInfoMessage>
                <Icon name="info" group="state" />
                <span>{translatedMDFValues.emailAgreementInfo}</span>
              </EmailInfoMessage>
            </InputDiv>

            <InputDiv style={{ gridColumn: 'span 2' }}>
              <label htmlFor="mcaCountry">
                {translatedMDFValues.country} <Asterisk>*</Asterisk>
              </label>
              <Select
                id="mcaCountry"
                name="mcaCountry"
                value={mcaFormData.mcaCountry}
                onChange={handleMcaInputChange('mcaCountry')}
                onBlur={handleMcaBlur('mcaCountry')}
                style={{ border: mcaTouched.mcaCountry && mcaErrors.mcaCountry ? '1px solid red' : '' }}
              >
                <option value="">{translatedMDFValues.selectCountryMCA}</option>
                {OPCO === 'IE' && <option value="IRL">{translatedMDFValues.ireland}</option>}
                {OPCO === 'PT' && <option value="PRT">{translatedMDFValues.portugal}</option>}
              </Select>
              {mcaTouched.mcaCountry && mcaErrors.mcaCountry && <ErrorMessage>{mcaErrors.mcaCountry}</ErrorMessage>}
            </InputDiv>
          </Grid>
        </StepContent>
      </TimelineStep>

      {/* Step 2: Accept Agreement - No dot, just content */}
      <TimelineStep>
        <StepContent>
          <AgreementSection>
            <AgreementTitle>{translatedMDFValues.acceptMCA}</AgreementTitle>
            <AgreementText>{translatedMDFValues.acceptMCADescription}</AgreementText>

            <ActionButtonWrapper>
              <Button
              text={translatedMDFValues.openAgreement}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenAgreement();
              }}
              state={!isStepCompleted(1) || isOpeningAgreement ? 'disabled' : undefined}
              loading={isOpeningAgreement}
              appearance="secondary"
              width="full"
              icon={{ name: 'pop-out', justify: 'left' }}
            />
            </ActionButtonWrapper>

            <InfoBox $type="info" style={{ marginTop: '16px' }}>
              <InLineNotification
                appearance="primary"
                text={translatedMDFValues.openAgreementInfo}
                state="info"
              />
            </InfoBox>
          </AgreementSection>
        </StepContent>
      </TimelineStep>

      {/* Step 3: Verify Agreement */}
      <TimelineStep>
        <TimelineDot />
        <StepContent>
          <AgreementSection>
            <AgreementTitle>{translatedMDFValues.verifyAcceptanceStatus}</AgreementTitle>
            <AgreementText>{translatedMDFValues.verifyAcceptanceDescription}</AgreementText>

            <ActionButtonWrapper>
              <Button
                text={isVerifying ? translatedMDFValues.verifying : translatedMDFValues.verifyStatus}
                onClick={handleVerifyStatus}
                state={isMcaVerified(agreementStatus) || isVerifying ? 'disabled' : undefined}
                appearance="secondary"
                icon={{ name: 'sync', justify: 'left' }}
                loading={isVerifying}
                width="full"
              />
            </ActionButtonWrapper>

            {isMcaVerified(agreementStatus) && (
               <InfoBox $type="success" style={{ marginTop: '16px' }}>
               <InLineNotification
                 appearance="primary"
                 text={translatedMDFValues.statusVerified}
                 state="success"
               />
             </InfoBox>
            )}

            {showVerificationError && agreementStatus === 'PENDING' && (
              <InfoBox $type="error" style={{ marginTop: '12px' }}>
                <InLineNotification
                  appearance="primary"
                  text={translatedMDFValues.verificationFailed}
                  state="error"
                />
              </InfoBox>
            )}
          </AgreementSection>
        </StepContent>
      </TimelineStep>
    </>
  );
};

export default MCAFormSection;
